import { useMemo, useState, useEffect } from 'react';
import { useDensity as useDensityContext } from '../contexts/DensityContext';
import { DensityMode, DensityHelpers } from '../types/density';
import {
  convertTailwindClass,
  convertTailwindClasses,
  getDensityPadding,
  getDensityMargin,
  getDensitySpacing,
  getDensityGap,
  getDensityHeight,
  getDensityFontSize,
  createDensityAwareClasses,
  getDensityMultipliers,
} from '../utils/densityHelpers';

/**
 * Enhanced useDensity hook with comprehensive helper functions
 * This is the primary hook components should use for density-aware styling
 */
export function useDensity(): DensityHelpers & {
  density: DensityMode;
  setDensity: (mode: DensityMode) => void;
  isCompact: boolean;
  isNormal: boolean;
  isSpaciousMode: boolean;
  config: any;
  multipliers: ReturnType<typeof getDensityMultipliers>;
  classes: (baseClasses: string, additionalClasses?: string) => string;
  responsive: (baseClasses: string) => string;
} {
  const context = useDensityContext();
  const { density, setDensity, config, isCompact, isNormal, isSpaciousMode } = context;

  // Memoize helper functions for performance
  const helpers: DensityHelpers = useMemo(
    () => ({
      getDensityPadding: (baseClass: string) => getDensityPadding(baseClass, density),
      getDensitySpacing: (baseClass: string) => getDensitySpacing(baseClass, density),
      getDensityMargin: (baseClass: string) => getDensityMargin(baseClass, density),
      getDensityFontSize: (baseClass: string) => getDensityFontSize(baseClass, density),
      getDensityGap: (baseClass: string) => getDensityGap(baseClass, density),
      getDensityHeight: (baseClass: string) => getDensityHeight(baseClass, density),
      convertTailwindClass: (
        baseClass: string,
        type?: 'padding' | 'margin' | 'spacing' | 'gap' | 'height' | 'font'
      ) => convertTailwindClass(baseClass, density, type),
    }),
    [density]
  );

  // Additional utility functions
  const multipliers = useMemo(() => getDensityMultipliers(density), [density]);

  const classes = useMemo(
    () => (baseClasses: string, additionalClasses?: string) =>
      createDensityAwareClasses(baseClasses, density, additionalClasses),
    [density]
  );

  const responsive = useMemo(
    () => (baseClasses: string) => convertTailwindClasses(baseClasses, density),
    [density]
  );

  return {
    ...helpers,
    density,
    setDensity,
    isCompact,
    isNormal,
    isSpaciousMode,
    config,
    multipliers,
    classes,
    responsive,
  };
}

/**
 * Hook for creating density-aware form styles
 */
export function useDensityFormStyles() {
  const { classes } = useDensity();

  return useMemo(
    () => ({
      input: classes(
        'px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500'
      ),
      button: classes(
        'px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'
      ),
      label: classes('text-sm font-medium text-gray-700 mb-1'),
      fieldGroup: classes('space-y-2'),
      formSection: classes('space-y-4 p-4 bg-white rounded-lg shadow'),
      buttonGroup: classes('flex space-x-3'),
      card: classes('p-6 bg-white rounded-lg shadow border'),
      cardHeader: classes('pb-4 border-b border-gray-200 mb-4'),
      grid: classes('grid gap-4'),
      gridCols2: classes('grid grid-cols-1 md:grid-cols-2 gap-4'),
      gridCols3: classes('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'),
    }),
    [classes]
  );
}

/**
 * Hook for creating density-aware table styles
 */
export function useDensityTableStyles() {
  const { classes } = useDensity();

  return useMemo(
    () => ({
      table: classes('min-w-full divide-y divide-gray-200'),
      th: classes(
        'px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
      ),
      td: classes('px-6 py-4 whitespace-nowrap text-sm text-gray-900'),
      row: 'hover:bg-gray-50',
      headerRow: classes('bg-gray-50'),
      pagination: classes('px-4 py-3 flex items-center justify-between border-t border-gray-200'),
      wrapper: classes('shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'),
    }),
    [classes]
  );
}

/**
 * Hook for creating density-aware modal/dialog styles
 */
export function useDensityModalStyles() {
  const { classes } = useDensity();

  return useMemo(
    () => ({
      overlay: 'fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50',
      container: 'fixed inset-0 z-50 overflow-y-auto',
      wrapper: 'flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0',
      modal: classes(
        'relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg'
      ),
      header: classes('px-4 py-4 sm:px-6'),
      body: classes('px-4 py-4 sm:px-6'),
      footer: classes('px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse bg-gray-50'),
      title: classes('text-lg font-medium text-gray-900'),
      closeButton: classes('absolute top-0 right-0 pt-4 pr-4'),
    }),
    [classes]
  );
}

/**
 * Hook for creating density-aware navigation styles
 */
export function useDensityNavStyles() {
  const { classes } = useDensity();

  return useMemo(
    () => ({
      navbar: classes('bg-white shadow px-4 py-2'),
      navItem: classes('px-3 py-2 rounded-md text-sm font-medium'),
      navItemActive: classes('px-3 py-2 rounded-md text-sm font-medium bg-blue-100 text-blue-700'),
      sidebar: classes('bg-gray-800 text-white w-64 min-h-screen p-4'),
      sidebarItem: classes('flex items-center px-2 py-2 text-sm font-medium rounded-md'),
      sidebarItemActive: classes(
        'flex items-center px-2 py-2 text-sm font-medium rounded-md bg-gray-900 text-white'
      ),
      breadcrumb: classes('flex space-x-2 text-sm text-gray-500 mb-4'),
    }),
    [classes]
  );
}

/**
 * Hook for creating density-aware layout styles
 */
export function useDensityLayoutStyles() {
  const { classes } = useDensity();

  return useMemo(
    () => ({
      container: classes('container mx-auto px-4'),
      section: classes('py-8'),
      header: classes('mb-6'),
      content: classes('space-y-6'),
      sidebar: classes('space-y-4'),
      footer: classes('mt-8 py-4 border-t border-gray-200'),
      pageTitle: classes('text-2xl font-bold text-gray-900 mb-2'),
      pageSubtitle: classes('text-gray-600 mb-4'),
      divider: classes('border-t border-gray-200 my-6'),
    }),
    [classes]
  );
}

/**
 * Hook for density-aware responsive design
 */
export function useDensityResponsive() {
  const { density, isCompact, isNormal, isSpaciousMode } = useDensity();
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setIsDesktop(width >= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return {
    density,
    isCompact,
    isNormal,
    isSpaciousMode,
    isMobile,
    isTablet,
    isDesktop,
    // Density-aware breakpoints
    shouldUseCompactLayout: isCompact || isMobile,
    shouldUseSpacious: isSpaciousMode && isDesktop,
    adaptiveColumns: isCompact ? 1 : isNormal ? 2 : 3,
  };
}

/**
 * Hook for creating density-aware component variants
 */
export function useDensityVariants<T extends Record<string, string>>(
  variants: Record<DensityMode, T>
): T {
  const { density } = useDensity();
  return variants[density];
}

// Re-export the context hook for convenience
// export { useDensity as useDensityContext } from '../contexts/DensityContext';
